import {
    selectedParametersListItemRemoveTwClass,
    selectedParametersListItemTwClass,
    selectedParametersListTwClass,
    selectedParametersNameTwClass,
} from 'components/Blocks/Product/Filter/SelectedParameters/SelectedFilters';
import { getEmptyDefaultProductFiltersMap } from 'config/constants';
import { ProductFilterOptionsFragmentApi } from 'graphql/generated';
import { formatParameterValue } from 'helpers/formaters/formatFilter';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import useTranslation from 'next-translate/useTranslation';
import { RemoveThinIcon } from 'public/svg/IconsSvg';
import { Fragment, memo } from 'react';
import { FilterOptionsParameterUrlQueryType } from 'types/productFilter';

export type SelectedFiltersParameterProps = {
    selectedParameter: FilterOptionsParameterUrlQueryType;
    filterOptions: ProductFilterOptionsFragmentApi;
};

export const SelectedFiltersParameter: FC<SelectedFiltersParameterProps> = memo(
    ({ selectedParameter, filterOptions }) => {
        const { t } = useTranslation();

        const selectedParameterOptions = filterOptions.parameters?.find(
            (parameterOption) => parameterOption.uuid === selectedParameter.parameter,
        );

        const { updateFilterParametersQuery } = useUpdateFilterQuery();

        const isSliderParameter = selectedParameterOptions?.__typename === 'ParameterSliderFilterOption';
        const isColorParameter = selectedParameterOptions?.__typename === 'ParameterColorFilterOption';
        const isCheckBoxParameter = selectedParameterOptions?.__typename === 'ParameterCheckboxFilterOption';
        const isLogicalType =
            selectedParameterOptions && 'isLogicalType' in selectedParameterOptions
                ? selectedParameterOptions.isLogicalType
                : false;

        const defaultProductFiltersMap = getEmptyDefaultProductFiltersMap();
        const selectedParameterValues =
            // hack typescript because it is confused about filtering shared types
            isCheckBoxParameter || isColorParameter
                ? (
                      selectedParameterOptions.values as {
                          uuid: string;
                          text: string;
                          isSelected: boolean;
                      }[]
                  ).filter((selectedParameterValue) => {
                      return (
                          selectedParameter.values?.includes(selectedParameterValue.uuid) ||
                          defaultProductFiltersMap.parameters
                              .get(selectedParameter.parameter)
                              ?.has(selectedParameterValue.uuid)
                      );
                  })
                : undefined;

        return (
            <>
                <Fragment key={selectedParameterOptions?.uuid}>
                    {isSliderParameter ? (
                        <ul className={selectedParametersListTwClass}>
                            <li className={selectedParametersNameTwClass}>{selectedParameterOptions.name}</li>
                            <li className={selectedParametersListItemTwClass}>
                                {selectedParameter.minimalValue && (
                                    <>
                                        <span>{t('from')}&nbsp;</span>
                                        {selectedParameter.minimalValue || selectedParameterOptions.minimalValue}
                                        {!!selectedParameterOptions.unit?.name &&
                                            `\xa0${selectedParameterOptions.unit.name}`}
                                    </>
                                )}

                                {selectedParameter.maximalValue && (
                                    <>
                                        <span>&nbsp;{t('to')}&nbsp;</span>
                                        {selectedParameter.maximalValue || selectedParameterOptions.maximalValue}
                                        {selectedParameterOptions.unit?.name &&
                                            `\xa0${selectedParameterOptions.unit.name}`}
                                    </>
                                )}

                                <RemoveThinIcon
                                    className={selectedParametersListItemRemoveTwClass}
                                    onClick={() =>
                                        updateFilterParametersQuery(selectedParameterOptions.uuid, undefined)
                                    }
                                />
                            </li>
                        </ul>
                    ) : (
                        <ul className={selectedParametersListTwClass}>
                            <li className={selectedParametersNameTwClass}>{selectedParameterOptions?.name}</li>
                            {selectedParameterValues?.map((selectedValue) => (
                                <li key={selectedValue.uuid} className={selectedParametersListItemTwClass}>
                                    {formatParameterValue(selectedValue.text, isLogicalType, t)}
                                    <RemoveThinIcon
                                        className={selectedParametersListItemRemoveTwClass}
                                        onClick={() =>
                                            updateFilterParametersQuery(selectedParameter.parameter, selectedValue.uuid)
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </Fragment>
            </>
        );
    },
);

SelectedFiltersParameter.displayName = 'Parameters';
